import React, { useId } from 'react'
import { useTranslation } from 'react-i18next'
import * as TOKENS from '../chat/design-tokens'
import { DotIcon } from '../DotIcon'
import { useTheme } from '../../context/ThemeContext.js'
import { LocaleToggle } from '../ui/LocaleToggle.js'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'

/* ── Types ──────────────────────────────────────────────────────────── */

interface LandingHeaderProps {
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  scrollToServices: () => void
  handleAboutNavigation: () => void
  scrollToContact: () => void
  onStartChat: () => void
}

/* ── Shared atoms ───────────────────────────────────────────────────── */

/** Nav text button — no transform, pure colour + bg transition at 150 ms */
const navBtnClass = `
  min-h-11 px-4 py-2 rounded-none
  ${TOKENS.TEXT_SIZE.sm} font-medium
  ${TOKENS.TEXT.secondary}
  transition-colors duration-150
  hover:text-foreground hover:bg-muted
  ${TOKENS.INTERACTION.focusRing}
`

/* ── Component ──────────────────────────────────────────────────────── */

export const LandingHeader: React.FC<LandingHeaderProps> = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  scrollToServices,
  handleAboutNavigation,
  scrollToContact,
  onStartChat,
}) => {
  const { isDarkMode, toggleTheme } = useTheme()
  const { t } = useTranslation('marketing')
  const { hero, aria } = useLandingContent()
  const mobileMenuId = useId()

  const navItems: Array<{ label: string; href?: string; action?: () => void }> = [
    { label: t('nav.services'), action: scrollToServices },
    { label: t('nav.workshops'), href: '/workshops' },
    { label: t('nav.about'), action: handleAboutNavigation },
    { label: t('nav.contact'), action: scrollToContact },
  ]

  return (
    <>
      {/* ── Desktop / global header ─────────────────────────────── */}
      <header className={`pointer-events-none fixed inset-x-0 top-0 ${TOKENS.Z_INDEX.modal}`}>
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 pt-4 sm:px-8 md:px-10 md:pt-6">
          {/* Brand */}
          <div className="pointer-events-auto hidden md:flex items-center">
            <span className="font-mono text-lg font-semibold tracking-tight text-foreground select-none">
              F.B<span className={TOKENS.BRAND.slash}>/c</span>
            </span>
          </div>

          {/* Desktop nav */}
          <div
            className={`
              pointer-events-auto hidden items-center ${TOKENS.GAP.sm}
              ${TOKENS.ANIMATION.fadeInUp} md:flex
            `}
          >
            <nav
              className={`
                mr-2 flex items-center gap-0.5 p-1
                border rounded-none ${TOKENS.BORDER.default} bg-background
              `}
              aria-label={aria.primaryNav}
            >
              {navItems.map(item =>
                item.href ? (
                  <a key={item.label} href={item.href} className={navBtnClass}>
                    {item.label}
                  </a>
                ) : (
                  <button type="button" key={item.label} onClick={item.action} className={navBtnClass}>
                    {item.label}
                  </button>
                )
              )}
            </nav>

            <LocaleToggle className="future-locale-toggle" />

            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className={`
                ${TOKENS.SCALE['2xl']} flex items-center justify-center rounded-none
                border ${TOKENS.BORDER.default} bg-background
                ${TOKENS.TEXT.secondary}
                transition-colors duration-150 hover:bg-muted hover:text-foreground
                ${TOKENS.INTERACTION.focusRing}
              `}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? t('nav.light') : t('nav.dark')}
            >
              {isDarkMode ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>

            <button
              type="button"
              onClick={onStartChat}
              className={`group relative overflow-hidden min-h-11 ${TOKENS.PATTERNS.marketingPrimary} px-5 py-2.5`}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative text-sm">{hero.ctas.primary}</span>
            </button>
          </div>

          {/* Mobile menu toggle */}
          <div
            className={`
              pointer-events-auto flex items-center ${TOKENS.GAP.sm}
              ${TOKENS.ANIMATION.fadeInUp} md:hidden
            `}
          >
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`
                flex h-10 w-10 items-center justify-center rounded-none
                border ${TOKENS.BORDER.default} bg-background
                transition-colors duration-150 hover:bg-muted
                ${TOKENS.INTERACTION.focusRing}
              `}
              aria-label={t('nav.toggleMenu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls={mobileMenuId}
            >
              {isMobileMenuOpen ? (
                <DotIcon name="close" size={20} />
              ) : (
                <DotIcon name="menu" size={20} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile menu overlay ──────────────────────────────────── */}
      {isMobileMenuOpen && (
        <div
          id={mobileMenuId}
          role="dialog"
          aria-modal="true"
          aria-label={aria.menuButton}
          className={`
            fixed inset-0 ${TOKENS.Z_INDEX.overlay}
            bg-background px-6 pb-8 pt-24
            md:hidden
          `}
        >
          <div className="mx-auto flex max-w-sm flex-col gap-6">
            {/* Nav links */}
            <nav
              className={`
                flex flex-col border rounded-none
                ${TOKENS.BORDER.default} bg-background
              `}
              aria-label={aria.mobileNav}
            >
              {navItems.map(item =>
                item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`
                      text-left px-5 py-4 text-base font-medium
                      ${TOKENS.TEXT.primary}
                      border-b last:border-b-0 ${TOKENS.BORDER.default}
                      transition-colors duration-150 hover:bg-muted
                      ${TOKENS.INTERACTION.focusRing}
                    `}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    type="button"
                    key={item.label}
                    onClick={() => {
                      item.action?.()
                      setIsMobileMenuOpen(false)
                    }}
                    className={`
                      text-left px-5 py-4 text-base font-medium
                      ${TOKENS.TEXT.primary}
                      border-b last:border-b-0 ${TOKENS.BORDER.default}
                      transition-colors duration-150 hover:bg-muted
                      ${TOKENS.INTERACTION.focusRing}
                    `}
                  >
                    {item.label}
                  </button>
                )
              )}
            </nav>

            {/* Theme row */}
            <div
              className={`
                flex items-center justify-between
                border-t pt-5 ${TOKENS.BORDER.default}
              `}
            >
              <span
                className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-wide ${TOKENS.TEXT.muted}`}
              >
                {t('nav.appearance')}
              </span>
              <button
                type="button"
                onClick={toggleTheme}
                className={`
                  group relative inline-flex items-center ${TOKENS.GAP.sm}
                  border rounded-none px-4 py-2 bg-background
                  ${TOKENS.TEXT_SIZE.sm} font-medium ${TOKENS.TEXT.secondary}
                  ${TOKENS.BORDER.default}
                  transition-colors duration-150 hover:bg-muted hover:text-foreground
                  ${TOKENS.INTERACTION.focusRing}
                `}
              >
                {isDarkMode ? t('nav.dark') : t('nav.light')}
                {isDarkMode ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Language row */}
            <div
              className={`
                flex items-center justify-between
                border-t pt-5 ${TOKENS.BORDER.default}
              `}
            >
              <span
                className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-wide ${TOKENS.TEXT.muted}`}
              >
                {t('nav.language')}
              </span>
              <LocaleToggle className="future-locale-toggle" />
            </div>

            {/* Primary CTA */}
            <button
              type="button"
              onClick={() => {
                onStartChat()
                setIsMobileMenuOpen(false)
              }}
              className={`group relative w-full overflow-hidden ${TOKENS.PATTERNS.marketingPrimary} px-6 py-4`}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative">{hero.ctas.primary}</span>
              <svg
                className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
